import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, UserMinus, GraduationCap } from "lucide-react";
import { toast } from "sonner";

// Nomina/revoca docenti tra gli utenti già registrati (nessun invito, nessuna email)
export const TeachersManager = ({ teachers, onChange }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null); // { type: "confirm-student" } | { type: "revoke", t }

  const nominate = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/teachers", { email: email.trim(), confirm });
      if (!teachers.some((t) => t.user_id === data.user_id)) onChange([...teachers, data]);
      setEmail(""); setAsk(null); toast.success(`${data.name || data.email} è ora docente`);
    } catch (err) {
      if (err.response?.status === 409) setAsk({ type: "confirm-student" });
      else toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const revoke = async (t) => {
    try {
      await api.delete(`/admin/teachers/${t.user_id}`);
      onChange(teachers.filter((x) => x.user_id !== t.user_id)); toast.success("Ruolo di docente revocato");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-3" data-testid="teachers-manager">
      <p className="text-sm font-medium text-foreground flex items-center gap-1.5"><GraduationCap size={16} className="text-primary" /> Docenti con accesso</p>
      <p className="text-xs text-muted-foreground">Indica l'email di un utente già registrato all'app. Nessun invito o email viene inviato: accederà con le sue credenziali e vedrà l'Area docente.</p>
      <form className="flex flex-col sm:flex-row gap-2" onSubmit={(e) => { e.preventDefault(); if (email.trim()) nominate(false); }}>
        <Input data-testid="teacher-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@esempio.it" className="bg-white" />
        <Button type="submit" size="sm" data-testid="teacher-add-btn" disabled={busy || !email.trim()} className="h-10"><UserPlus size={14} className="mr-1" /> Nomina docente</Button>
      </form>
      {teachers.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="teachers-empty">Nessun docente nominato.</p> : (
        <ul className="divide-y divide-border">
          {teachers.map((t) => (
            <li key={t.user_id} data-testid={`teacher-row-${t.email}`} className="py-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm break-words min-w-0"><span className="text-foreground font-medium">{t.name || "—"}</span> <span className="text-muted-foreground">{t.email}</span></span>
              <Button size="sm" variant="outline" data-testid={`teacher-revoke-${t.email}`} onClick={() => setAsk({ type: "revoke", t })}><UserMinus size={14} className="mr-1" /> Revoca</Button>
            </li>
          ))}
        </ul>
      )}
      <AlertDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)}>
        <AlertDialogContent data-testid="teacher-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{ask?.type === "revoke" ? "Revocare il ruolo di docente?" : "Questo utente ha dati da allievo"}</AlertDialogTitle>
            <AlertDialogDescription>
              {ask?.type === "revoke"
                ? `${ask.t.name || ask.t.email} perderà subito l'accesso all'Area docente e i suoi corsi resteranno senza account collegato (il nome del docente resta scritto).`
                : "Ha già un'iscrizione, pagamenti o prenotazioni di prova. Se lo nomini docente questi dati restano salvati ma spariscono dalle liste degli allievi; ricompaiono se revochi il ruolo."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="teacher-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="teacher-confirm-ok" onClick={() => (ask?.type === "revoke" ? revoke(ask.t) : nominate(true))}>
              {ask?.type === "revoke" ? "Revoca" : "Nomina comunque"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
